import {
    Check as CheckIcon,
    ContentCopy as CopyIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useState } from 'react';
import CodeBlock from './CodeBlock';

const StructuredApiEndpoint = ({
    method = 'GET',
    path = '',
    description,
    children,
    baseUrl = 'https://app.demandlogy.com/api',
    authorization = null,
    headerParams = [],
    queryParams = [],
    bodyParams = null,
    responseExamples = []
}) => {
    const [copied, setCopied] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        authorization: true,
        headerParams: true,
        queryParams: true,
        bodyParams: true,
        responses: true
    });

    const getMethodColor = (method) => {
        const colors = {
            'GET': 'primary.main',
            'POST': '#ff9800',
            'PUT': '#2196f3',
            'DELETE': '#f44336'
        };
        return colors[method] || '#666';
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const fullUrl = `${baseUrl}${path}`;

    return (
        <Box sx={{ mb: 4 }}>
            {/* API Header */}
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Chip
                        label={method}
                        size="small"
                        sx={{
                            backgroundColor: getMethodColor(method),
                            color: 'white',
                            fontWeight: 'bold',
                            minWidth: '60px'
                        }}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: '#1f2937'
                        }}
                    >
                        {path}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => copyToClipboard(fullUrl)}
                        sx={{ color: '#6b7280' }}
                    >
                        {copied ? <CheckIcon /> : <CopyIcon />}
                    </IconButton>
                </Stack>
                {description && (
                    <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                        {description}
                    </Typography>
                )}
            </Box>

            {/* Request Section */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1f2937' }}>
                Request
            </Typography>

            {/* Authorization Section */}
            {authorization && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <Box
                        onClick={() => toggleSection('authorization')}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#f9fafb',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Authorization
                        </Typography>
                        {expandedSections.authorization ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={expandedSections.authorization}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
                                {authorization.description}
                            </Typography>
                            <Box sx={{
                                p: 1.5,
                                backgroundColor: '#f3f4f6',
                                borderRadius: '6px',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                color: '#374151'
                            }}>
                                {authorization.example}
                            </Box>
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* Header Params Section */}
            {headerParams.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <Box
                        onClick={() => toggleSection('headerParams')}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#f9fafb',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Header Params
                        </Typography>
                        {expandedSections.headerParams ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={expandedSections.headerParams}>
                        <Box sx={{ p: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Required</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {headerParams.map((param, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <Typography sx={{
                                                    fontFamily: 'monospace',
                                                    fontWeight: 600,
                                                    color: '#3b82f6'
                                                }}>
                                                    {param.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{param.type}</TableCell>
                                            <TableCell>
                                                {param.required ? (
                                                    <Chip
                                                        label="✓"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#dcfce7',
                                                            color: '#166534',
                                                            minWidth: '30px'
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Optional"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#f3f4f6',
                                                            color: '#6b7280',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: '#4b5563' }}>
                                                    {param.description}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* Query Params Section */}
            {queryParams.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <Box
                        onClick={() => toggleSection('queryParams')}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#f9fafb',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Query Params
                        </Typography>
                        {expandedSections.queryParams ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={expandedSections.queryParams}>
                        <Box sx={{ p: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Required</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {queryParams.map((param, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <Typography sx={{
                                                    fontFamily: 'monospace',
                                                    fontWeight: 600,
                                                    color: '#3b82f6'
                                                }}>
                                                    {param.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{param.type}</TableCell>
                                            <TableCell>
                                                {param.required ? (
                                                    <Chip
                                                        label="✓"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#dcfce7',
                                                            color: '#166534',
                                                            minWidth: '30px'
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Optional"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#f3f4f6',
                                                            color: '#6b7280',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: '#4b5563' }}>
                                                    {param.description}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* Body Params Section */}
            {bodyParams && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <Box
                        onClick={() => toggleSection('bodyParams')}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#f9fafb',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                                Request Body
                            </Typography>
                            <Chip
                                label={bodyParams.contentType || 'application/json'}
                                size="small"
                                sx={{
                                    backgroundColor: '#e5e7eb',
                                    color: '#374151',
                                    fontSize: '0.75rem'
                                }}
                            />
                        </Stack>
                        {expandedSections.bodyParams ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={expandedSections.bodyParams}>
                        <Box sx={{ p: 2 }}>
                            {bodyParams.description && (
                                <Typography variant="body2" sx={{ color: '#4b5563', mb: 2 }}>
                                    {bodyParams.description}
                                </Typography>
                            )}
                            {children}
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* Additional Content / Examples for GET or when bodyParams is null */}
            {!bodyParams && children && (
                <Box sx={{ mb: 3 }}>
                    {children}
                </Box>
            )}

            {/* Responses Section */}
            {responseExamples.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <Box
                        onClick={() => toggleSection('responses')}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#f9fafb',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Responses
                        </Typography>
                        {expandedSections.responses ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Collapse in={expandedSections.responses}>
                        <Box sx={{ p: 2 }}>
                            {responseExamples.map((response, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Box sx={{
                                        p: 1.5,
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer'
                                    }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: response.status === 200 || response.status === 201 ? '#10b981' : '#f59e0b'
                                            }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                                                {response.status} {response.statusText}
                                            </Typography>
                                        </Stack>
                                        <ExpandMoreIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ mt: 1 }}>
                                        <CodeBlock
                                            title="Response Example"
                                            code={JSON.stringify(response.example, null, 2)}
                                            showTitle={false}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Collapse>
                </Paper>
            )}
        </Box>
    );
};

export default StructuredApiEndpoint;
